import {
  MedicalCasePhaseDocumentState,
  MedicalCasePhases,
  MedicalCaseProcessingPhase,
} from '../../../domain/medical-case/model';
import {
  MedicalCasePipelineFlow,
  PreparationStepKey,
  STEP_TO_PHASE_MAP,
  WAYBACK_STEP_TO_PHASE_MAP,
} from '../model/pipeline-step.model';
import { MedicalCaseDocumentResolver } from './medical-case-document-resolver.util';

export interface StepBadgeStatus {
  status: 'idle' | 'processing' | 'completed' | 'error';
  fileIds: string[];
  allConfirmed: boolean;
}

export type StepPhaseStatusResult = StepBadgeStatus;

/**
 * @Applied: [PR1] - Canonical rules for document and phase statuses.
 * Centralizes how we interpret backend states (PENDING, COMPLETED, CONFIRMED, FAILED)
 * into UI-level statuses (badge, row state, eligibility).
 */
export class MedicalCaseStatusResolver {
  /**
   * Derives the overall status for a pipeline step based on its documents' states.
   */
  static getStepBadgeStatus(
    phases: MedicalCasePhases | null | undefined,
    step: PreparationStepKey,
    flow: MedicalCasePipelineFlow = 'preparation'
  ): StepBadgeStatus {
    const phase = flow === 'wayback'
      ? WAYBACK_STEP_TO_PHASE_MAP[step]
      : STEP_TO_PHASE_MAP[step];
    if (!phases || !phase) {
      return { status: 'idle', fileIds: [], allConfirmed: false };
    }

    const phaseData = phases[phase];
    if (!phaseData) {
      return { status: 'idle', fileIds: [], allConfirmed: false };
    }

    const docs = phaseData.documents ?? [];
    const states = docs.map(d => d.activeVersion?.state).filter((s): s is MedicalCasePhaseDocumentState => !!s);
    const fileIds = docs.map(d => d.activeVersion?.fileId).filter((f): f is string => !!f);

    if (states.length === 0) {
      return {
        status: phaseData.state === 'PENDING' ? 'processing' : 'idle',
        fileIds,
        allConfirmed: false,
      };
    }

    if (states.some(s => s === 'FAILED')) {
      return { status: 'error', fileIds, allConfirmed: false };
    }

    if (states.some(s => s === 'PENDING')) {
      return { status: 'processing', fileIds, allConfirmed: false };
    }

    if (states.every(s => s === 'COMPLETED' || s === 'CONFIRMED')) {
      const allConfirmed = states.every(s => s === 'CONFIRMED');
      return { status: 'completed', fileIds, allConfirmed };
    }

    return { status: 'idle', fileIds, allConfirmed: false };
  }

  /**
   * Determines if a document is "outdated" (needs re-processing due to changes in previous steps).
   */
  static isDocumentOutdated(
    phases: MedicalCasePhases | null | undefined,
    originalId: string | null | undefined,
    phase?: MedicalCaseProcessingPhase
  ): boolean {
    if (!phases || !originalId) return false;

    if (phase) {
      const doc = MedicalCaseDocumentResolver.getDocumentInPhase(phases, phase, originalId);
      return doc?.activeVersion?.outdated === true;
    }

    // If no phase specified, check if it's outdated in ANY phase
    return Object.keys(phases).some(p => 
      this.isDocumentOutdated(phases, originalId, p as MedicalCaseProcessingPhase)
    );
  }

  /**
   * Determines if a document can be selected/submitted in the current phase.
   * Eligibility depends on:
   * 1. Existence in the current phase (activeVersion present)
   * 2. Not being in a terminal/final state if the UI requires fresh submission
   * 3. Previous phase requirements (business rule: must be confirmed in previous visible phase)
   */
  static isDocumentEligibleForAction(
    phases: MedicalCasePhases | null | undefined,
    originalId: string | null | undefined,
    currentPhase: MedicalCaseProcessingPhase
  ): boolean {
    const doc = MedicalCaseDocumentResolver.getDocumentInPhase(phases, currentPhase, originalId);
    if (!doc || !doc.activeVersion) return false;

    // Logic for eligibility would go here, centralizing condition trees from review-list etc.
    // For now, mirroring existing common checks:
    return doc.activeVersion.state !== 'PENDING' && doc.activeVersion.state !== 'FAILED';
  }
}
